import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import izenuAPI from '../../services/izenuAPI';

// Catálogo → Lançamentos: every album, EP and single on the platform, drafts
// included. A row opens /release/{id}, the page the "Release" button in
// Músicas already used.

const TYPE_LABEL = { single: 'Single', ep: 'EP', album: 'Álbum' };
const PER_PAGE = 50;

const selectCls = 'text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white';

const StatusBadge = ({ status }) =>
    status === 'draft' ? (
        <span className="text-xs font-semibold text-gray-500 bg-gray-400/20 border border-gray-400 rounded px-2 py-0.5">Rascunho</span>
    ) : (
        <span className="text-xs font-semibold text-green-600 bg-green-400/20 border border-green-500 rounded px-2 py-0.5">Publicado</span>
    );

const price = (r) => {
    if (!r.for_sale) return <span className="text-gray-400">—</span>;
    const parts = [];
    if (r.price_aoa) parts.push(`${Number(r.price_aoa).toLocaleString('pt-PT')} Kz`);
    if (r.price_usd) parts.push(`$${Number(r.price_usd).toFixed(2)}`);
    return <span className="text-amber-700 font-medium whitespace-nowrap">{parts.join(' · ') || 'À venda'}</span>;
};

export default function AdminReleases() {
    const navigate = useNavigate();
    const [q, setQ] = useState('');
    const [status, setStatus] = useState('');
    const [type, setType] = useState('');
    const [sale, setSale] = useState('');
    const [pending, setPending] = useState(false);
    const [page, setPage] = useState(1);

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => setPage(1), [q, status, type, sale, pending]);

    useEffect(() => {
        const timer = setTimeout(async () => {
            setLoading(true);
            setError('');
            try {
                const { data } = await izenuAPI.get('/admin/releases/list.php', {
                    params: { q, status, type, sale, pending: pending ? 1 : '', page, per_page: PER_PAGE },
                });
                if (!data.response) throw new Error(data.message);
                setRows(data.data);
                setTotal(data.total);
                setPages(Math.max(1, data.pages));
            } catch (e) {
                setError('Não foi possível carregar os lançamentos.');
            }
            setLoading(false);
        }, q ? 400 : 0);
        return () => clearTimeout(timer);
    }, [q, status, type, sale, pending, page]);

    return (
        <div className="p-6">
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 24, fontWeight: 700, color: '#333', marginBottom: 8 }}>Lançamentos</h2>
                <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, marginBottom: 12 }}>
                    Todos os álbuns, EPs e singles da plataforma, incluindo rascunhos. Clique numa linha para abrir o lançamento.
                </p>
                <span style={{ fontSize: 13, color: '#6366f1', fontWeight: 600 }}>
                    {total.toLocaleString('pt-PT')} {total === 1 ? 'lançamento' : 'lançamentos'}
                </span>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Pesquisar por título, artista ou #id..."
                    className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white"
                    style={{ minWidth: 280 }}
                />
                <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
                    <option value="">Publicados e rascunhos</option>
                    <option value="active">Publicados</option>
                    <option value="draft">Rascunhos</option>
                </select>
                <select value={type} onChange={(e) => setType(e.target.value)} className={selectCls}>
                    <option value="">Todos os tipos</option>
                    <option value="album">Álbuns</option>
                    <option value="ep">EPs</option>
                    <option value="single">Singles</option>
                </select>
                <select value={sale} onChange={(e) => setSale(e.target.value)} className={selectCls}>
                    <option value="">À venda ou não</option>
                    <option value="yes">À venda</option>
                    <option value="no">Não está à venda</option>
                </select>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={pending} onChange={(e) => setPending(e.target.checked)} />
                    Com faixas por aprovar
                </label>
            </div>

            {error && <p className="text-sm text-rose-600 mb-3">{error}</p>}

            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs text-gray-500 uppercase bg-gray-50 border-b">
                            <tr>
                                <th className="px-3 py-3">Capa</th>
                                <th className="px-3 py-3">Título / Artista</th>
                                <th className="px-3 py-3">Tipo</th>
                                <th className="px-3 py-3">Estado</th>
                                <th className="px-3 py-3">Faixas</th>
                                <th className="px-3 py-3">Venda</th>
                                <th className="px-3 py-3 hidden lg:table-cell">Criado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && rows.length === 0 ? (
                                <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">A carregar…</td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">Nenhum lançamento com estes filtros.</td></tr>
                            ) : rows.map((r) => (
                                <tr
                                    key={r.id}
                                    onClick={() => navigate(`/release/${r.id}`)}
                                    className="border-b last:border-0 hover:bg-amber-50/50 cursor-pointer"
                                    style={{ opacity: loading ? 0.5 : 1 }}
                                >
                                    <td className="px-3 py-2">
                                        {r.artwork ? (
                                            <img src={r.artwork} alt="" className="w-12 h-12 rounded object-cover bg-gray-100" loading="lazy" />
                                        ) : (
                                            <div className="w-12 h-12 rounded bg-gray-100" />
                                        )}
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="font-medium text-gray-900">{r.title}</div>
                                        <div className="text-xs text-gray-500">
                                            <Link
                                                to={`/list-musics?user_id=${r.artist_id}&artist=${encodeURIComponent(r.artist_name || '')}`}
                                                onClick={(e) => e.stopPropagation()}
                                                className="hover:text-indigo-600 hover:underline"
                                                title="Ver as músicas deste artista"
                                            >
                                                {r.artist_name || `#${r.artist_id}`}
                                            </Link>
                                            {r.distributor_name && <span className="text-gray-400"> · {r.distributor_name}</span>}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-gray-700">{TYPE_LABEL[r.type] || r.type}</td>
                                    <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        {r.track_count}
                                        {r.pending_count > 0 && (
                                            <span className="ml-2 text-xs font-semibold text-amber-600 bg-amber-400/20 border border-amber-500 rounded px-1.5 py-0.5">
                                                {r.pending_count} por aprovar
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2">{price(r)}</td>
                                    <td className="px-3 py-2 text-gray-500 hidden lg:table-cell whitespace-nowrap">
                                        {r.created_at ? new Date(r.created_at.replace(' ', 'T')).toLocaleDateString('pt-PT') : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {pages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                        <button
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page <= 1}
                            className="text-sm font-medium px-3 py-1.5 rounded border border-gray-300 text-gray-600 disabled:text-gray-300 disabled:border-gray-200"
                        >
                            Anterior
                        </button>
                        <span className="text-sm text-gray-600">
                            Página <strong>{page}</strong> de <strong>{pages}</strong>
                        </span>
                        <button
                            onClick={() => setPage((p) => Math.min(pages, p + 1))}
                            disabled={page >= pages}
                            className="text-sm font-medium px-3 py-1.5 rounded border border-gray-300 text-gray-600 disabled:text-gray-300 disabled:border-gray-200"
                        >
                            Seguinte
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
